import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { testimonials, Testimonial } from '../data/content';
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 out of 5 stars" data-id="element-169" data-mp="3fxkusbssu7ki">
      {Array.from({
      length: 5
    }).map((_, index) => <StarIcon key={index} className="h-[18px] w-[18px] fill-gold text-gold" aria-hidden="true" data-id="element-170" data-mp="36yzgwty9vgq2" data-mpi={index} />)}
    </div>;
}
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <article className="flex h-full flex-col rounded-2xl border border-gold/35 bg-navy p-6 shadow-sm shadow-black/25" data-id="element-171" data-mp="2x0jlies7plar">
      <Stars data-id="element-172" data-mp="2s2i21sqvjjyr" />
      <p className="mt-4 flex-1 text-sm leading-relaxed text-white/80" data-id="element-173" data-mp="2s308tjb5xgkx">“{testimonial.text}”</p>
      <div className="mt-6 flex items-center gap-3" data-id="element-174" data-mp="v8589t0m550o">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-sm font-bold text-navy" data-id="element-175" data-mp="qac2h1382d0x">
          {testimonial.initials}
        </span>
        <div data-id="element-176" data-mp="h816hkw3kaiw">
          <p className="text-sm font-bold text-white" data-id="element-177" data-mp="h8avp69bidnj">{testimonial.name}</p>
          <p className="text-xs text-white/60" data-id="element-178" data-mp="m6cj23ds0z4t">{testimonial.city}</p>
          <p className="text-xs italic text-gold" data-id="element-179" data-mp="78ttiyt9yhwb">{testimonial.role}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  const [index, setIndex] = useState(0);
  const activeTestimonial = testimonials[index % testimonials.length];
  const go = useCallback((direction: number) => {
    setIndex(current => (current + direction + testimonials.length) % testimonials.length);
  }, []);
  return <section className="w-full bg-navy-mid px-5 py-12 sm:py-20" data-id="element-180" data-mp="q6cvjihbf098">
      <div className="mx-auto max-w-5xl" data-id="element-181" data-mp="v4o4zs3p78qx">
        <div className="mx-auto max-w-2xl text-center" data-id="element-182" data-mp="v3mrlgz7ma61">
          <h2 className="font-display text-2xl font-bold leading-tight text-white sm:text-4xl" data-id="element-183" data-mp="1iu8z975axsfi">
            What ESL teachers around the world are saying
          </h2>
          <p className="mt-3 text-sm text-white/70 sm:text-base" data-id="element-184" data-mp="1xhquzg0vzcps">
            <span className="text-gold" data-id="element-185" data-mp="1xfwlqyyz991j">⭐⭐⭐⭐⭐</span> 4.9/5 · Trusted by 500+ teachers in 7
            countries
          </p>
        </div>
        <div className="mt-10 hidden gap-6 md:grid md:grid-cols-3" data-id="element-186" data-mp="1ogt29tvwermi">
          {testimonials.map(testimonial => <TestimonialCard key={testimonial.name} testimonial={testimonial} data-id="element-187" data-mp="1jh6cym2spkxc" />)}
        </div>
        <div className="mt-10 md:hidden" data-id="element-188" data-mp="1ek6gn2vqzwjf">
          <div className="relative overflow-hidden" data-id="element-189" data-mp="1ekzh5m445pt4">
            <AnimatePresence mode="wait" data-id="element-190" data-mp="19rdd2ic0mu8x">
              <motion.div key={activeTestimonial.name} initial={{
              opacity: 0,
              x: 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -40
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-191" data-mp="19qbtfpgxozfl">
                <TestimonialCard testimonial={activeTestimonial} data-id="element-192" data-mp="vuhsbsmejed1" />
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-5 flex items-center justify-between" data-id="element-193" data-mp="32hp4myip25m4">
            <button type="button" onClick={() => go(-1)} aria-label="Previous testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/50 text-gold" data-id="element-194" data-mp="37g0evacwii4v">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-195" data-mp="3gg5ayocn9u3q" />
            </button>
            <div className="flex gap-2" data-id="element-196" data-mp="2jrphwiabt2lv">
              {testimonials.map((testimonial, itemIndex) => <button type="button" key={testimonial.name} onClick={() => setIndex(itemIndex)} aria-label={`Go to testimonial ${itemIndex + 1}`} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${itemIndex === index ? 'w-6 bg-gold' : 'w-2 bg-gold/35'}`} data-id="element-197" data-mp="2oq0swwccxj5o" data-mpi={itemIndex} />)}
            </div>
            <button type="button" onClick={() => go(1)} aria-label="Next testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/50 text-gold" data-id="element-198" data-mp="1lw3nre6k119v">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-199" data-mp="1vojb6r0mei25" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}